import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '../../lib/api.js';
import { sfContentDocumentUrl, sfLightningBaseUrl } from '../../lib/sf-lightning.js';

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${Math.round(kb)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

export function HandoverSfFilePanel({
  projectId,
  salesforceUrl,
}: {
  projectId: string;
  salesforceUrl: string | null;
}) {
  const { t } = useTranslation();
  const filesQuery = useQuery({
    queryKey: ['v2-sf-files', projectId],
    queryFn: () => api.listSfFiles(projectId),
    staleTime: 30_000,
  });
  const lightningBase = sfLightningBaseUrl(salesforceUrl);

  return (
    <div className="mt-3">
      <h4 className="text-xs font-medium mb-1.5" style={{ color: 'var(--ink-2)' }}>
        {t('v2.handover.sf_files_title', 'Dateien in Salesforce')}
      </h4>

      {filesQuery.isLoading && (
        <p className="text-sm" style={{ color: 'var(--ink-3)' }}>
          {t('v2.detail.loading', 'Lädt…')}
        </p>
      )}

      {filesQuery.isError && (
        <p className="text-sm" style={{ color: 'var(--danger)' }}>
          {t('v2.handover.sf_files_error', 'Die Salesforce-Dateien konnten nicht geladen werden.')}
        </p>
      )}

      {!filesQuery.isLoading &&
        !filesQuery.isError &&
        (filesQuery.data?.items.length ?? 0) === 0 && (
          <p className="text-sm" style={{ color: 'var(--ink-3)' }}>
            {t('v2.handover.sf_files_empty', 'Keine Dateien am Salesforce-Projekt.')}
          </p>
        )}

      {!filesQuery.isLoading && !filesQuery.isError && (filesQuery.data?.items.length ?? 0) > 0 && (
        <ul className="flex flex-wrap gap-2">
          {filesQuery.data?.items.map((f) => {
            const href =
              lightningBase && f.content_document_id
                ? sfContentDocumentUrl(lightningBase, f.content_document_id)
                : null;
            const meta = `${f.type} · ${formatBytes(f.size_bytes)}`;
            return (
              <li
                key={f.content_version_id}
                className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded max-w-full min-w-0"
                style={{ background: 'var(--surface-2)' }}
                title={meta}
              >
                {href ? (
                  <a
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm truncate underline"
                    style={{ color: 'var(--primary)' }}
                    title={t('v2.handover.sf_files_open', 'In Salesforce ansehen')}
                  >
                    {f.name}
                  </a>
                ) : (
                  <span className="text-sm truncate" style={{ color: 'var(--ink-1)' }}>
                    {f.name}
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
